/* =============================================================================
   SJ Coal Tree hero (Lump O' Coal)
   Headline entrance, once, when the hero is first on screen:
     "Give" "them" "joy" pop in word by word ("for Christmas!" not yet),
     "joy" gets a red X, "coal" drops in above it with one small bounce
     while the crossed-out "joy" is nudged down out of its way (line two
     isn't showing yet, so there's room), then "joy" and its X fade while
     "coal" settles into its slot, and finally "for" "Christmas!" pop in. It ends on the untouched
     approved headline.
   Layers (theme assets, all full-headline canvases so they register exactly):
     -arch-w-give/-them/-for/-christmas, -arch-coal  each word cut from the
         approved headline's own pixels (together they rebuild it exactly)
     -arch-joy        joy, scaled and tilted into coal's slot on the arch
     -arch-joke.json  joy/coal boxes + tilt, used to place the X
   The X lives outside the screen-blended headline so it reads on top
   of the white letters. Reduced motion, no JS, failed or slow loads,
   background tabs and late scrolls all show the final headline as-is.
   Below the hero: benefit cards and ribbon curls reveal once on scroll, and
   the mug steam pauses offscreen (initScrollMotion). The purchase panel and
   the shared sections' reveals are handled by sj-xmas.js.
   ============================================================================= */
(function () {
  'use strict';
  if (window.__sjcLoaded) return;
  window.__sjcLoaded = true;

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
  var EASE_IN_OUT = 'cubic-bezier(0.77, 0, 0.175, 1)';

  // Timeline (ms)
  // crossedOut: time to take in the scratched-out "joy" before "coal" arrives
  var T = { wordGap: 170, word: 380, read: 420, scribble: 320, crossedOut: 1000, drop: 450, hold: 300, settle: 450, beforeLine2: 750 };

  function decodeImg(img) {
    if (!img) return Promise.resolve();
    if (img.complete && img.naturalWidth) {
      return img.decode ? img.decode().catch(function () {}) : Promise.resolve();
    }
    return new Promise(function (resolve, reject) {
      img.addEventListener('load', function () {
        (img.decode ? img.decode() : Promise.resolve()).catch(function () {}).then(resolve);
      }, { once: true });
      img.addEventListener('error', reject, { once: true });
    });
  }

  function layer(src) {
    var img = document.createElement('img');
    img.className = 'sjc-joke-layer';
    img.alt = '';
    img.decoding = 'async';
    img.src = src;
    return img;
  }

  // Load the geometry and decode every layer before touching the headline.
  var WORDS = ['give', 'them', 'for', 'christmas'];
  var ALL = ['give', 'them', 'joy', 'coal', 'for', 'christmas'];
  function loadJoke(head) {
    var url = head.getAttribute('data-sjc-joke');
    if (!url || !window.fetch) return Promise.reject();
    var parts = { joy: layer(head.getAttribute('data-sjc-joy')), coal: layer(head.getAttribute('data-sjc-coal')) };
    WORDS.forEach(function (w) { parts[w] = layer(head.getAttribute('data-sjc-' + w)); });
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('joke');
      return r.json();
    }).then(function (geo) {
      return Promise.all(ALL.map(function (k) { return decodeImg(parts[k]); }))
        .then(function () { parts.geo = geo; return parts; });
    });
  }

  function initHeadline(root) {
    var head = root.querySelector('[data-sjc-headline]');
    var scene = root.querySelector('[data-sjc-scene]');
    var fx = root.querySelector('[data-sjc-headfx]');
    if (!head) return;
    var html = document.documentElement;

    function showStatic() {
      head.classList.add('is-live');
      html.classList.remove('sjc-intro');
    }
    if (!html.classList.contains('sjc-intro') || reduceMQ.matches || !head.animate || !fx) {
      showStatic();
      return;
    }

    var gaveUp = false;
    // A slow connection shouldn't produce a late, distracting gag.
    var giveUp = setTimeout(function () { gaveUp = true; showStatic(); }, 2500);

    Promise.all([decodeImg(head.querySelector('.sjc-headline__img')), loadJoke(head)])
      .then(function (res) {
        if (gaveUp) return;
        clearTimeout(giveUp);
        var r = (scene || head).getBoundingClientRect();
        var vh = window.innerHeight || html.clientHeight;
        // Already scrolled past, or opened in a background tab: just show it.
        if (r.bottom < 0 || r.top > vh || document.visibilityState === 'hidden') {
          showStatic();
          return;
        }
        requestAnimationFrame(function () { play(head, scene, fx, res[1]); });
      })
      .catch(function () {
        if (gaveUp) return;
        clearTimeout(giveUp);
        showStatic();
      });
  }

  // A word pops up into place: short rise, tiny overshoot, settle.
  function wordIn(el, delay) {
    var a = el.animate([
      { opacity: 0, transform: 'translateY(7%)' },
      { opacity: 1, transform: 'translateY(-0.8%)', offset: 0.7 },
      { opacity: 1, transform: 'none' }
    ], { duration: T.word, delay: delay, easing: EASE_OUT, fill: 'both' });
    // Write the resting state to the element and release the animation, so
    // finished words aren't kept on held compositor layers.
    a.addEventListener('finish', function () {
      el.style.opacity = '1';
      el.style.transform = 'none';
      a.cancel();
    });
    return a;
  }

  function play(head, scene, fx, p) {
    var html = document.documentElement;
    var base = head.querySelector('.sjc-headline__img');
    var geo = p.geo;

    // Build the headline from its words, each hidden until its turn.
    ALL.forEach(function (k) {
      p[k].style.opacity = '0';
      head.appendChild(p[k]);
    });
    if (base) base.style.opacity = '0';
    head.classList.add('is-live');
    html.classList.remove('sjc-intro');

    // The X spans the whole word, tilted with the arch.
    var svg = fx.querySelector('.sjc-scribble');
    var strokes = svg.querySelectorAll('path');
    var jb = geo.joy;
    var rot = 'rotate(' + geo.angle + 'deg)';
    svg.style.cssText = 'left:' + (jb.x - jb.w * 0.05) + '%;top:' + (jb.y + jb.h * 0.02) + '%;' +
      'width:' + (jb.w * 1.1) + '%;height:' + (jb.h * 0.96) + '%;transform:' + rot + ';';
    fx.classList.add('is-on');

    // How far above its slot "coal" waits: about its x-height, never past the
    // top of the scene (which clips).
    var hb = head.getBoundingClientRect();
    var top = scene ? scene.getBoundingClientRect().top : hb.top;
    var coalTopPx = hb.top - top + (geo.coal.y / 100) * hb.height;
    var lift = Math.min((geo.coal.h / 100) * hb.height * 0.72, coalTopPx - 2);
    var liftPct = (lift / hb.height) * 100;      // translate % of the (full-canvas) layer
    // ...and how far the crossed-out joy steps down so the raised coal never
    // touches it (its j dot sits higher than coal's slot).
    var raisedCoalBottom = geo.coal.y + geo.coal.h - liftPct;
    var nudgePct = Math.max(0, raisedCoalBottom - geo.joy.y + 3);
    var nudgePx = nudgePct / 100 * hb.height;

    // 1. "Give" "them" "joy"
    var t = 80;
    wordIn(p.give, t); t += T.wordGap;
    wordIn(p.them, t); t += T.wordGap;
    wordIn(p.joy, t);
    t += T.word + T.read;

    // 2. red X: one stroke, then the other
    var half = Math.round(T.scribble / 2);
    // (strokes stay hidden by CSS until they start: a zero-length round cap
    // would otherwise show as a red dot)
    strokes[0].animate([{ strokeDashoffset: 1, opacity: 1 }, { strokeDashoffset: 0, opacity: 1 }],
      { duration: half, delay: t, easing: 'cubic-bezier(0.45, 0, 0.2, 1)', fill: 'forwards' });
    strokes[1].animate([{ strokeDashoffset: 1, opacity: 1 }, { strokeDashoffset: 0, opacity: 1 }],
      { duration: half, delay: t + half + 50, easing: 'cubic-bezier(0.45, 0, 0.2, 1)', fill: 'forwards' });
    t += T.scribble + 50 + T.crossedOut;

    // 3. coal drops a short way into place above joy, one small bounce
    p.coal.animate([
      { opacity: 0, transform: 'translateY(' + (-liftPct - 9) + '%)' },
      { opacity: 1, transform: 'translateY(' + (-liftPct + 1.6) + '%)', offset: 0.62 },
      { opacity: 1, transform: 'translateY(' + (-liftPct - 0.8) + '%)', offset: 0.82 },
      { opacity: 1, transform: 'translateY(' + (-liftPct) + '%)' }
    ], { duration: T.drop, delay: t, easing: EASE_OUT, fill: 'both' });
    // ...as the crossed-out joy (and its X) steps down out of the way
    if (nudgePct > 0) {
      p.joy.animate([{ transform: 'none' }, { transform: 'translateY(' + nudgePct + '%)' }],
        { duration: T.drop * 0.8, delay: t + 60, easing: EASE_OUT, fill: 'forwards' });
      svg.animate([{ transform: rot }, { transform: 'translateY(' + nudgePx + 'px) ' + rot }],
        { duration: T.drop * 0.8, delay: t + 60, easing: EASE_OUT, fill: 'forwards' });
    }
    t += T.drop + T.hold;

    // 4. joy and its X bow out; coal settles into the original slot
    p.joy.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, delay: t, easing: 'ease-out', fill: 'forwards' });
    svg.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, delay: t, easing: 'ease-out', fill: 'forwards' });
    p.coal.animate([
      { opacity: 1, transform: 'translateY(' + (-liftPct) + '%)' },
      { opacity: 1, transform: 'none' }
    ], { duration: T.settle, delay: t, easing: EASE_IN_OUT, fill: 'forwards' });
    t += Math.round(T.settle * 0.55) + T.beforeLine2;

    // 5. "for" "Christmas!"
    wordIn(p['for'], t); t += T.wordGap;
    var last = wordIn(p.christmas, t);

    // End on the untouched approved headline (the words are its own pixels).
    last.addEventListener('finish', function () {
      if (base) base.style.opacity = '';
      requestAnimationFrame(function () {
        ALL.forEach(function (k) { if (p[k].parentNode) p[k].parentNode.removeChild(p[k]); });
        fx.classList.remove('is-on');
      });
    });
  }

  // ── Scroll motion below the hero ─────────────────────────────────────────
  // [data-sjc-rise] (benefit cards, ribbon curls) are hidden only once armed
  // here, then revealed once when they first scroll into view. The mug steam
  // pauses whenever the benefits band is off screen.
  function initScrollMotion() {
    if (!('IntersectionObserver' in window)) return;
    var rises = document.querySelectorAll('.sjc [data-sjc-rise]');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12 });
    rises.forEach(function (el) {
      // Already on screen at load (e.g. a tall desktop window): no entrance.
      var r = el.getBoundingClientRect();
      if (r.top < (window.innerHeight || 0) && r.bottom > 0) return;
      el.classList.add('is-armed');
      io.observe(el);
    });

    var steamIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.target.classList.toggle('is-offscreen', !e.isIntersecting); });
    });
    document.querySelectorAll('.sjc-benefits').forEach(function (el) { steamIO.observe(el); });
  }

  function boot() {
    document.querySelectorAll('.sjc[data-sjc-hero]').forEach(initHeadline);
    initScrollMotion();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
